import { useCallback, useEffect, useState } from 'react';
import { Input } from '../components/ui/Input/Input';
import {
  PrimaryButton,
  TextButton,
  TextSecondaryButton,
  WhiteButton
} from '../components/ui/Button/Buttons';
import {
  LeftArrow,
  PlusBlue,
  ResendIcon,
  Search,
  SortIcon,
  SparklesIcon,
  TrashCan
} from '../icons/SvgIcons';
import DropdownPortal from '../components/ui/DropdownPortal/DropdownPortal';
import { SearchSection } from '../components/features/SearchSection/SearchSection';
import { useAppSelector } from '../hooks/redux';
import { useDispatch } from 'react-redux';
import {
  setAssignAiSystemWithoutInvitationSuccess,
  setCurrentUser,
  setIsRolePerAiSystemChangedSuccess,
  setRemoveUserFromSingleAiSystemSuccess
} from '../store/slices/userManagementSlice';
import {
  AiSystemsForUserDTO,
  Role,
  UpdateUserRoleRequest
} from '../types/Team.types';
import Loader from '../components/ui/Loader/Loader';
import { useUserManagement } from '../components/hooks/useUserManagement';
import ModalWarning from '../components/ui/Modal/ModalWarning';
import { setShowWarningModal } from '../store/slices/accountSlice';
import { Toast } from '../components/ui/Toast/Toast';
import { EmptyState } from '../components/ui/EmptyState/EmptyState';
import Modal from '../components/ui/Modal/Modal';
import { InviteUser } from '../components/UserManagement/InviteUser';
import { searchAiSystemsForUser } from '../services/TeamService';
import { TruncatedText } from '../utils/TruncatedText';
import { AssignUser } from '../components/UserManagement/AssignUser';

export function RegisteredUserProfile() {
  const dispatch = useDispatch();
  const {
    currentUser,
    aiSystemsForUser,
    aiSystemsForUserLoading,
    isRolePerAiSystemChangedSuccess,
    removeUserFromSingleAiSystemError,
    removeUserFromSingleAiSystemLoading,
    removeUserFromSingleAiSystemSuccess,
    assignAiSystemWithoutInvitationSuccess,
    sorting,
    teamRoles
  } = useAppSelector((state) => state.userManagement);

  const account = useAppSelector((state) => state.accountDefault.account);

  const { total } = useAppSelector((state) => state.aiSystems);

  const {
    sendRoleChangesPerAiSystem,
    removeUserFromSingleAiSystem,
    fetchAiSystemsForUser,
    fetchTeamMembers,
    deleteTeamMemberFromAccount,
    resendInvitationForMember
  } = useUserManagement();

  const [firstName, setFirstName] = useState(currentUser?.firstName || '-');
  const [lastName, setLastName] = useState(currentUser?.lastName || '-');
  const [email, setEmail] = useState(currentUser?.email || '');
  const [searchQuery, setSearchQuery] = useState('');
  const [aiSystemsSearchResults, setAiSystemsSearchResults] =
    useState<AiSystemsForUserDTO | null>(null);

  const [aiSystems, setAiSystems] = useState<AiSystemsForUserDTO | null>(
    aiSystemsForUser
  );

  const [isRoleChanged, setIsRoleChanged] = useState(false);
  const [bodyRequestChangeRoles, setBodyRequestChangeRoles] =
    useState<UpdateUserRoleRequest>({ updates: [] });

  const [showToast, setShowToast] = useState(false);
  const [systemToRemove, setSystemToRemove] = useState<string>('');
  const [lastSystemToRemove, setLastSystemToRemove] = useState(false);
  const [openModalForAssignSystem, setOpenModalForAssignSystem] =
    useState(false);

  const showWarningModal = useAppSelector(
    (state) => state.account.showWarningModal
  );

  const [deletingMember, setDeletingMember] = useState(false);
  const [assignedSystemToast, setAssignedSystemToast] = useState(false);
  const [removedSystemToast, setRemovedSystemToast] = useState(false);

  const isAllSystemsAssigned = aiSystems
    ? aiSystems.items.length >= total
    : false;

  const handleRoleChanges = () => {
    if (bodyRequestChangeRoles.updates.length === 0) return;

    if (!account || !currentUser) return;
    sendRoleChangesPerAiSystem(
      account.id,
      currentUser.userId,
      bodyRequestChangeRoles
    );
  };

  useEffect(() => {
    if (removeUserFromSingleAiSystemError) {
      console.error(
        'Error removing user from AI system:',
        removeUserFromSingleAiSystemError
      );
      return;
    }

    if (account && currentUser) {
      fetchAiSystemsForUser(account.id, currentUser.userId);
    }
  }, [removeUserFromSingleAiSystemError]);

  useEffect(() => {
    if (removeUserFromSingleAiSystemSuccess) {
      if (account && currentUser) {
        fetchAiSystemsForUser(account.id, currentUser.userId);
        fetchTeamMembers(account.id, sorting.sortOrder);
        setRemovedSystemToast(true);
        dispatch(setRemoveUserFromSingleAiSystemSuccess(false));
      }
    }
  }, [removeUserFromSingleAiSystemSuccess]);

  useEffect(() => {
    if (isRolePerAiSystemChangedSuccess) {
      setShowToast(true);
      setIsRoleChanged(false);
      setBodyRequestChangeRoles({ updates: [] });
      dispatch(setIsRolePerAiSystemChangedSuccess(false));
    }
  }, [isRolePerAiSystemChangedSuccess, account, currentUser]);

  const handleCancel = () => {
    if (!aiSystemsForUser) return;
    setAiSystems(aiSystemsForUser);
    setIsRoleChanged(false);
    setBodyRequestChangeRoles({ updates: [] });
  };

  const roles = [
    { value: 'EDITOR', label: 'Editor' },
    { value: 'VIEWER', label: 'Viewer' }
  ];

  useEffect(() => {
    setAiSystems(aiSystemsForUser);
    setShowToast(false);
  }, [aiSystemsForUser]);

  useEffect(() => {
    if (!aiSystems || !aiSystemsForUser) {
      setIsRoleChanged(false);
      setBodyRequestChangeRoles({ updates: [] });
      return;
    }

    const updates: { aiSystemId: string; role: Role }[] = [];

    for (const currentSystem of aiSystems.items) {
      const originalSystem = aiSystemsForUser.items.find(
        (original) => original.aiSystemId === currentSystem.aiSystemId
      );

      if (originalSystem && originalSystem.role !== currentSystem.role) {
        updates.push({
          aiSystemId: currentSystem.aiSystemId,
          role: currentSystem.role
        });
      }
    }

    const hasChanges = updates.length > 0;

    setIsRoleChanged(hasChanges);
    setBodyRequestChangeRoles({ updates });
  }, [aiSystems]);

  const handleBack = () => {
    dispatch(setCurrentUser(null));
    setLastSystemToRemove(false);
  };

  const handleDelete = () => {
    dispatch(setShowWarningModal(true));
    setDeletingMember(true);
  };

  function confirmDelete() {
    dispatch(setShowWarningModal(false));
    deleteTeamMemberFromAccount(account!.id, currentUser!.userId);
    setDeletingMember(false);
    handleBack();
  }

  const handleAssignSystem = () => {
    setOpenModalForAssignSystem(true);
  };

  const handleRemoveSystem = () => {
    dispatch(setShowWarningModal(false));
    removeUserFromSingleAiSystem(
      account!.id,
      currentUser!.userId,
      systemToRemove,
      lastSystemToRemove
    );
    setSystemToRemove('');
    if (lastSystemToRemove) {
      handleBack();
    }
  };

  const openRemoveSystemModal = (systemId: string) => {
    setSystemToRemove(systemId);
    setLastSystemToRemove(aiSystems?.items.length === 1);
    dispatch(setShowWarningModal(true));
  };

  const handleRoleChange = (systemId: string, newRole: string) => {
    if (!aiSystems) return;

    const updatedItems = aiSystems.items.map((system) =>
      system.aiSystemId === systemId
        ? { ...system, role: newRole as Role }
        : system
    );

    setAiSystems({
      ...aiSystems,
      items: updatedItems
    });
  };

  const handleResendInvitation = () => {
    if (!account || !currentUser) return;
    resendInvitationForMember(account.id, currentUser.userId);
  };

  const displayedAiSystems =
    searchQuery.length > 0 ? aiSystemsSearchResults : aiSystems;

  const handleSearchChange = useCallback(
    async (query: string) => {
      if (query.length === 0) {
        setAiSystemsSearchResults(null);
        return;
      }

      // Add null checks before accessing properties
      if (!currentUser || !account) {
        console.warn('Cannot search: currentUser or account is null');
        return;
      }

      console.log('Searching AI systems for id:', currentUser.userId);
      try {
        const response = await searchAiSystemsForUser(
          account.id,
          currentUser.userId,
          query
        );
        setAiSystemsSearchResults(response.data);
      } catch (error) {
        console.error('Error searching users:', error);
        setAiSystemsSearchResults(null);
      }
    },
    [account, currentUser]
  );

  useEffect(() => {
    if (!currentUser) {
      setSearchQuery('');
      setAiSystemsSearchResults(null);
    }
  }, [currentUser]);

  useEffect(() => {
    if (assignAiSystemWithoutInvitationSuccess) {
      if (account && currentUser) {
        fetchAiSystemsForUser(account.id, currentUser.userId);
        fetchTeamMembers(account.id, sorting.sortOrder);
        setAssignedSystemToast(true);
        dispatch(setAssignAiSystemWithoutInvitationSuccess(false));
      }
    }
  }, [assignAiSystemWithoutInvitationSuccess]);

  return (
    <div className="flex flex-col w-full bg-white p-4 rounded-lg">
      <div className="w-full mx-auto text-left">
        {/* Header */}
        <div className="flex items-center self-stretch flex-row mb-5 justify-between">
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={handleBack}
              id="registered-user-profile-back-button"
              data-testid="registered-user-profile-back-button"
              className="flex gap-2 items-center text-red"
            >
              {LeftArrow()}
            </button>
            <h1 className="text-base font-medium leading-6 text-gray-950">
              {currentUser?.isRegistered
                ? currentUser.firstName + ' ' + currentUser.lastName
                : currentUser?.email}
            </h1>
          </div>
          <div className="flex flex-row gap-2">
            {!currentUser?.isRegistered && (
              <TextButton
                onClick={handleResendInvitation}
                icon={<ResendIcon />}
              >
                Resend Invitation
              </TextButton>
            )}
            <button
              onClick={handleDelete}
              aria-label="Delete user"
              id="registered-user-profile-delete-button"
              data-testid="registered-user-profile-delete-button"
            >
              {TrashCan('small')}
            </button>
          </div>
        </div>

        <div
          className={`flex flex-col gap-8 px-6 mb-8 overflow-y-auto ${
            isRoleChanged ? 'h-[calc(100vh-330px)]' : 'h-[calc(100vh-270px)]'
          }`}
        >
          {/* Personal Information */}
          <div className="bg-white">
            <h2 className="text-base font-medium leading-6 mb-6 text-gray-900v2">
              Personal information
            </h2>
            <div className="grid grid-cols-2 gap-6 mb-6">
              <Input
                label="Name"
                id="firstName"
                type="text"
                required={true}
                value={firstName}
                maxLength={50}
                onChange={(e) => setFirstName(e.target.value)}
                labelClassName="label"
                inputClassName="input"
                disabled
              />
              <Input
                label="Last name"
                id="lastName"
                type="text"
                required={true}
                value={lastName}
                maxLength={50}
                onChange={(e) => setLastName(e.target.value)}
                labelClassName="label"
                inputClassName="input"
                disabled
              />
            </div>
            <div className="max-w-[calc(50%-12px)]">
              <Input
                label="Email address"
                id="email"
                type="email"
                required={true}
                value={email}
                maxLength={254}
                onChange={(e) => setEmail(e.target.value)}
                labelClassName="label"
                inputClassName="input"
                disabled
              />
            </div>
          </div>

          {/* AI Systems Assigned */}
          <div className="bg-white">
            {aiSystemsForUserLoading ||
            removeUserFromSingleAiSystemLoading ||
            aiSystems === null ? (
              <div className="absolute top-1/2 left-1/2">
                <Loader />
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-base font-medium leading-6 text-gray-900v2">
                    AI systems Assigned
                  </h2>
                  <TextButton
                    onClick={handleAssignSystem}
                    className="text-purple-800"
                    size="xs"
                    iconPosition="left"
                    icon={PlusBlue(isAllSystemsAssigned)}
                    disabled={isAllSystemsAssigned}
                  >
                    Assign AI System
                  </TextButton>
                </div>

                {/* Search */}
                <div className="mb-4">
                  <SearchSection
                    searchValue={searchQuery}
                    onSearchChange={(value) => {
                      setSearchQuery(value);
                      handleSearchChange(value);
                    }}
                    placeholder="Search by name"
                  />
                </div>

                {/* Table */}

                {displayedAiSystems?.items.length === 0 ? (
                  <EmptyState
                    icon={searchQuery ? <Search /> : <SparklesIcon />}
                    title={
                      searchQuery
                        ? 'No AI Systems matched your criteria.'
                        : 'No AI Systems assigned.'
                    }
                    description={
                      searchQuery
                        ? 'Try adjusting your search.'
                        : 'Assign an AI system to enable access and permissions.'
                    }
                    classes="max-h-[200px] flex-1"
                  />
                ) : (
                  <div className="overflow-hidden border border-gray-250v2 rounded-lg">
                    <table className="w-full table-fixed">
                      <thead className="bg-gray-40 border-b border-gray-250v2">
                        <tr>
                          <th className="w-[calc((100%-116px)/2)] px-6 py-3 text-left text-xs font-medium text-gray-550 uppercase tracking-wider">
                            <div className="flex items-center gap-2">
                              SYSTEM NAME
                              <button
                                id="registered-user-profile-sort-system-name-button"
                                data-testid="registered-user-profile-sort-system-name-button"
                              >
                                <SortIcon />
                              </button>
                            </div>
                          </th>
                          <th className="w-[calc((100%-116px)/2)] px-6 py-3 text-left text-xs font-medium text-gray-550 uppercase tracking-wider">
                            ROLE
                          </th>
                          <th className="w-[116px] px-6 py-3 text-left text-xs font-medium text-gray-550 uppercase tracking-wider">
                            ACTIONS
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-250v2">
                        {displayedAiSystems?.items.map((system) => (
                          <tr key={system.aiSystemName}>
                            <td className="w-[calc((100%-116px)/2)] px-6 py-4 text-sm font-medium text-gray-900v2 align-middle">
                              <TruncatedText text={system.aiSystemName} />
                            </td>
                            <td className="w-[calc((100%-116px)/2)] px-6 py-4 text-sm text-gray-550 align-middle">
                              <div className="w-36">
                                <DropdownPortal
                                  options={roles.filter(
                                    (role) => role.value !== system.role
                                  )}
                                  allOptions={roles}
                                  value={system.role}
                                  onChange={(e) =>
                                    handleRoleChange(system.aiSystemId, e)
                                  }
                                  size="sm"
                                  buttonClassName="h-[32px] px-[10px] py-[6px]"
                                />
                              </div>
                            </td>
                            <td className="w-[116px] px-3 py-4 text-sm align-middle">
                              <TextSecondaryButton
                                size="xs"
                                iconPosition="left"
                                onClick={() =>
                                  openRemoveSystemModal(system.aiSystemId)
                                }
                                className="text-red-600 hover:text-red-800 font-medium"
                              >
                                Remove
                              </TextSecondaryButton>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        {isRoleChanged && (
          <div className="flex justify-between items-center self-stretch pt-5 border-t border-gray-250v2">
            <WhiteButton onClick={handleCancel}>Cancel</WhiteButton>
            <PrimaryButton onClick={handleRoleChanges}>
              Save Changes
            </PrimaryButton>
          </div>
        )}
      </div>

      {showWarningModal && systemToRemove && (
        <ModalWarning
          title="Remove AI System?"
          boldDescription="Are you sure you want to remove AI system?"
          description={
            lastSystemToRemove
              ? 'User will no longer have access to data or any associated resources, including AI systems, risk assessment records, reports, and shared content.'
              : 'User will lose permission to access this AI system and all related resources, including data, risk assessment records, reports, and shared content.'
          }
          buttonText="Remove"
          handleButton={handleRemoveSystem}
        />
      )}
      {showWarningModal && deletingMember && (
        <ModalWarning
          title="Delete User?"
          boldDescription="Are you sure you want to delete user from your EU AI Audit account?"
          description="User will no longer have access to data or any associated resources, including AI systems, risk assessment records, reports, and shared content."
          handleButton={confirmDelete}
          buttonText="Delete User"
        />
      )}
      {openModalForAssignSystem && (
        <Modal
          open={openModalForAssignSystem}
          setOpen={setOpenModalForAssignSystem}
          dialogPanelClassnames="min-w-[820px] overflow-hidden"
        >
          <AssignUser
            setIsOpen={setOpenModalForAssignSystem}
            registeredUserEmail={currentUser?.email}
            assignListForAiSystemFromUser={aiSystems?.items.map(
              (item) => ({ id: item.aiSystemId }) as any
            )}
            userRoles={teamRoles?.aiSystemRoles || []}
          />
        </Modal>
      )}
      {showToast && <Toast showToast={showToast} setShowToast={setShowToast} />}
      {assignedSystemToast && (
        <Toast
          text="AI System(s) successfully assigned!"
          showToast={assignedSystemToast}
          setShowToast={setAssignedSystemToast}
        />
      )}
      {removedSystemToast && (
        <Toast
          text="AI System successfully removed!"
          showToast={removedSystemToast}
          setShowToast={setRemovedSystemToast}
        />
      )}
    </div>
  );
}
